/**
 * A resolution that came back as an error carries the in-code default, which for
 * config stores is indistinguishable from a genuine `control` assignment. Callers
 * use this to avoid persisting a value the provider never actually resolved.
 */
const ERROR_REASON = 'ERROR'

export interface ConfigStorePaths {
  /** The resolved flag value. Part of the cache identity. */
  variant: string
  /** False when the provider errored, meaning `variant` is only a fallback. */
  cacheable: boolean
  /** Candidate config URLs: the resolved variant first, `control` as fallback. */
  urls: string[]
}

/**
 * Builds the cache identity for a config store. `control` — and an unresolved flag —
 * keep the un-suffixed key so existing stored data stays valid; a real variant gets
 * its own slot, so an override and control can never overwrite each other.
 */
export function getConfigStoreCacheKey(
  configStoreKey: string,
  variant: string
): string {
  if (!variant || variant === 'control') {
    return configStoreKey
  }
  return `${configStoreKey}@${variant}`
}

export async function getConfigStorePaths(
  configStoreKey: string
): Promise<ConfigStorePaths> {
  const { client } = await useOpenFeature()
  const baseURL = (useRuntimeConfig().app.baseURL || '').replace(/\/$/, '')
  const details = client.getStringDetails(configStoreKey, 'control')
  const variant = details.value
  const path = `${baseURL}/api/v2/configs/${configStoreKey}`
  return {
    variant,
    cacheable: details.reason !== ERROR_REASON,
    urls: [`${path}/${variant}.json`, `${path}/control.json`],
  }
}
